"use client";

import {
  IconArrowNarrowLeft,
  IconArrowNarrowRight,
  IconX,
} from "@tabler/icons-react";
import "aos/dist/aos.css";
import { AnimatePresence, motion } from "framer-motion";
import PropTypes from "prop-types";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useOutsideClick } from "../../../hooks/use-outside-click";
import { cn } from "../../../lib/utils";

// Carousel Context
export const CarouselContext = createContext({
  onCardClose: () => {},
  currentIndex: 0,
});

// Carousel Component
export const Carousel = ({ items }) => {
  const carouselRef = React.useRef(null);
  const cardRef = React.useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);
  const [isAnimating, setIsAnimating] = useState(false);
  const [cardWidth, setCardWidth] = useState(400); // default fallback
  const [visibleCount, setVisibleCount] = useState(1);
  const [scrollDirection, setScrollDirection] = useState('right'); // Add direction state
  const autoScrollTimeoutRef = useRef(null);

  // Dynamically calculate visible count and card width
  useEffect(() => {
    function updateSizes() {
      if (carouselRef.current && cardRef.current) {
        // Ensure cardRef.current has a valid offsetWidth before calculation
        const cWidth = cardRef.current.offsetWidth > 0 ? cardRef.current.offsetWidth + 16 : 400 + 16; // 16 = gap
        setCardWidth(cWidth);
        const vCount = Math.floor(carouselRef.current.offsetWidth / cWidth);
        setVisibleCount(vCount > 0 ? vCount : 1);
      }
    }
    updateSizes();
    window.addEventListener('resize', updateSizes);
    return () => window.removeEventListener('resize', updateSizes);
  }, []);

  // Clone visibleCount cards at both ends for infinite loop effect
  const headClones = items.slice(-visibleCount);
  const tailClones = items.slice(0, visibleCount);
  const infiniteItems = [...headClones, ...items, ...tailClones];

  // On mount or visibleCount change, scroll to the first real card
  useEffect(() => {
    if (carouselRef.current && cardWidth > 0) { // Ensure cardWidth is calculated
      carouselRef.current.scrollLeft = cardWidth * visibleCount;
      setCurrentIndex(visibleCount); // Set initial index to the first actual item
    }
  }, [cardWidth, visibleCount]);

  // True infinite auto-scroll with back-and-forth motion
  useEffect(() => {
    let interval;
    if (autoScrollEnabled && !isAnimating) {
      interval = setInterval(() => {
        if (!carouselRef.current) return;

        // Clear any pending delay when a new scroll is initiated
        if (autoScrollTimeoutRef.current) {
          clearTimeout(autoScrollTimeoutRef.current);
          autoScrollTimeoutRef.current = null;
        }

        setIsAnimating(true);
        carouselRef.current.scrollBy({
          left: scrollDirection === 'right' ? cardWidth : -cardWidth,
          behavior: "smooth"
        });

        // After scroll animation
        setTimeout(() => {
          setIsAnimating(false);
          const totalCards = items.length;
          const currentScrollLeft = carouselRef.current.scrollLeft;
          const endOfRealCardsScroll = cardWidth * (visibleCount + totalCards - 1);
          const startOfRealCardsScroll = cardWidth * visibleCount;

          // Check if we've reached either end
          if (scrollDirection === 'right' && currentScrollLeft >= endOfRealCardsScroll - (cardWidth * 0.1)) {
            // Reached the end, change direction to left
            setScrollDirection('left');
          } else if (scrollDirection === 'left' && currentScrollLeft <= startOfRealCardsScroll + (cardWidth * 0.1)) {
            // Reached the start, change direction to right
            setScrollDirection('right');
          }
        }, 800);
      }, 3000);
    }
    return () => {
      clearInterval(interval);
      if (autoScrollTimeoutRef.current) {
        clearTimeout(autoScrollTimeoutRef.current);
        autoScrollTimeoutRef.current = null;
      }
    };
  }, [autoScrollEnabled, isAnimating, items.length, cardWidth, visibleCount, scrollDirection]);

  // Handle seamless looping for manual scroll
  const handleScroll = () => {
    if (!carouselRef.current) return;

    // Clear any pending delay if user manually scrolls
    if (autoScrollTimeoutRef.current) {
      clearTimeout(autoScrollTimeoutRef.current);
      autoScrollTimeoutRef.current = null;
    }

    const totalCards = items.length;
    const scrollLeft = carouselRef.current.scrollLeft;

    // Position where the tail clones begin (after all original items)
    const startOfTailClones = cardWidth * (visibleCount + totalCards);
    // Position where the head clones end (before all original items)
    const endOfHeadClones = cardWidth * (visibleCount - 1); // This is roughly the end of the last head clone

    if (scrollLeft >= startOfTailClones) {
      // Jump from tail clones back to the beginning of real items
      carouselRef.current.scrollLeft = cardWidth * visibleCount;
    } else if (scrollLeft <= endOfHeadClones) {
      // Jump from head clones to the end of real items
      carouselRef.current.scrollLeft = cardWidth * (totalCards + visibleCount -1); // Adjusted for clarity
    }
  };

  const scrollLeftBtn = () => {
    if (carouselRef.current && !isAnimating) {
      // Clear any pending delay when user clicks left
      if (autoScrollTimeoutRef.current) {
        clearTimeout(autoScrollTimeoutRef.current);
        autoScrollTimeoutRef.current = null;
      }
      setIsAnimating(true);
      carouselRef.current.scrollBy({
        left: -cardWidth,
        behavior: "smooth"
      });
      setTimeout(() => {
        setIsAnimating(false);
        handleScroll(); // Trigger handleScroll to manage the loop jump
      }, 800);
    }
  };

  const scrollRightBtn = () => {
    if (carouselRef.current && !isAnimating) {
      // Clear any pending delay when user clicks right
      if (autoScrollTimeoutRef.current) {
        clearTimeout(autoScrollTimeoutRef.current);
        autoScrollTimeoutRef.current = null;
      }
      setIsAnimating(true);
      carouselRef.current.scrollBy({
        left: cardWidth,
        behavior: "smooth"
      });
      setTimeout(() => {
        setIsAnimating(false);
        handleScroll(); // Trigger handleScroll to manage the loop jump
      }, 800);
    }
  };

  const handleCardClose = (index) => {
    if (carouselRef.current) {
      // When a card is closed, scroll to it if necessary.
      // This part might need re-evaluation if the goal is to center the closed card.
      // For now, it scrolls to the card's position in the infiniteItems array.
      const scrollPosition = cardWidth * (index + visibleCount); // Adjusted to account for clones
      carouselRef.current.scrollTo({
        left: scrollPosition,
        behavior: "smooth"
      });
      // setCurrentIndex(index); // This might not be accurate for the visible card after close
    }
  };

  return (
    <CarouselContext.Provider value={{ onCardClose: handleCardClose, currentIndex }}>
      <div
        className="relative w-full transition-all duration-300"
        onMouseEnter={() => {
          setAutoScrollEnabled(false);
          // Clear delay timeout if mouse enters during the 5-sec pause
          if (autoScrollTimeoutRef.current) {
            clearTimeout(autoScrollTimeoutRef.current);
            autoScrollTimeoutRef.current = null;
          }
        }}
        onMouseLeave={() => setAutoScrollEnabled(true)}
      >
        <div
          className="flex w-full overflow-x-scroll overscroll-x-auto py-10 scroll-smooth [scrollbar-width:none]"
          ref={carouselRef}
          onScroll={handleScroll}
        >
          {/* Gradient overlay for visual effect, adjusted to match a typical fade */}
          <div
            className={cn(
              "absolute left-0 top-0 bottom-0 z-[1000] w-[5%] bg-gradient-to-r from-white dark:from-base_900 to-transparent opacity-100 pointer-events-none",
              "transition-all duration-300"
            )}
          ></div>
          <div
            className={cn(
              "absolute right-0 top-0 bottom-0 z-[1000] w-[5%] bg-gradient-to-l from-white dark:from-base_900 to-transparent opacity-100 pointer-events-none",
              "transition-all duration-300"
            )}
          ></div>

          <div
            className={cn(
              "flex flex-row justify-start gap-4 pl-4", // Added pl-4 to give space for first card
              "mx-auto transition-all duration-300"
            )}
          >
            {infiniteItems.map((item, index) => (
              <motion.div
                ref={index === visibleCount ? cardRef : null} // cardRef points to the first *real* item
                initial={{ opacity: 0, y: 20 }}
                animate={{
                  opacity: 1,
                  y: 0,
                  transition: {
                    duration: 0.5,
                    // Delay based on position relative to the *real* items for initial load animation
                    delay: 0.1 * (index >= visibleCount && index < visibleCount + items.length ? index - visibleCount : 0),
                    ease: "easeOut",
                    // only apply once on initial load
                    once: true,
                  },
                }}
                key={"card" + index}
                className={cn(
                  "last:pr-[5%] md:last:pr-[33%] rounded-3xl", // Adds padding at the end
                  "transition-all duration-300"
                )}
              >
                {item}
              </motion.div>
            ))}
          </div>
        </div>
        <div className="flex justify-end gap-2 mr-10">
          <button
            className={cn(
              "relative z-40 h-10 w-10 rounded-full bg-base_900 text-white flex items-center justify-center disabled:opacity-50",
              "transition-all duration-300 hover:scale-110 hover:bg-base_800",
              "focus:outline-none focus:ring-2 focus:ring-base_500 focus:ring-offset-2"
            )}
            onClick={scrollLeftBtn}
            disabled={isAnimating}
          >
            <IconArrowNarrowLeft className="h-6 w-6" />
          </button>
          <button
            className={cn(
              "relative z-40 h-10 w-10 rounded-full bg-base_900 text-white flex items-center justify-center disabled:opacity-50",
              "transition-all duration-300 hover:scale-110 hover:bg-base_800",
              "focus:outline-none focus:ring-2 focus:ring-base_500 focus:ring-offset-2"
            )}
            onClick={scrollRightBtn}
            disabled={isAnimating}
          >
            <IconArrowNarrowRight className="h-6 w-6" />
          </button>
        </div>
      </div>
    </CarouselContext.Provider>
  );
};

// Card Component
export const Card = ({ card, index, layout = false }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const { onCardClose } = useContext(CarouselContext);

  useEffect(() => {
    function onKeyDown(event) {
      if (event.key === "Escape") {
        handleClose();
      }
    }

    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  useOutsideClick(containerRef, () => handleClose());

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    // When closing, call onCardClose, but consider if `index` needs to be
    // the original `items` index or the `infiniteItems` index.
    // For now, it passes the original `index`.
    onCardClose(index);
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <div className="fixed inset-0 h-screen z-[500] overflow-auto rounded-xl">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="bg-white/20 backdrop-blur-lg h-full w-full fixed inset-0"
            />
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              ref={containerRef}
              layoutId={layout ? `card-${card.assignment_name}` : undefined}
              className="max-w-5xl mx-auto h-fit z-[60] my-4 md:my-10 p-4 md:p-8 lg:p-12 rounded-3xl font-sans relative bg-white dark:bg-base_900 shadow-2xl"
            >
              {/* Close Button */}
              <button
                className="sticky top-4 h-10 w-10 right-4 md:right-8 lg:right-12 ml-auto bg-base_600 rounded-full flex items-center justify-center hover:bg-base_700 transition-colors"
                onClick={handleClose}
              >
                <IconX className="h-6 w-6 text-neutral-100 dark:text-neutral-900" />
              </button>

              {/* Company Logo */}
              {card.company_logo && (
                <div className="flex justify-center mb-4 md:mb-8">
                  <img
                    src={card.company_logo}
                    alt={`${card.assignment_with} Logo`}
                    className="h-16 w-16 md:h-20 md:w-20 object-contain rounded-full border-2 border-base_200 dark:border-base_700"
                  />
                </div>
              )}

              {/* Assignment Name */}
              <motion.h1
                layoutId={layout ? `title-${card.assignment_name}` : undefined}
                className="text-xl md:text-xl lg:text-3xl font-bold text-neutral-800 dark:text-white text-center mb-2 md:mb-4"
              >
                {card.assignment_name}
              </motion.h1>

              {/* Assignment With (Company Name) */}
              <motion.p
                layoutId={layout ? `category-${card.assignment_name}` : undefined}
                className="text-base md:text-lg font-medium text-neutral-600 dark:text-neutral-300 text-center mb-4 md:mb-8"
              >
                {card.assignment_with}
              </motion.p>

              {/* Assignment Dates */}
              <div className="flex flex-col md:flex-row justify-center gap-4 md:gap-6 mb-4 md:mb-8">
                <div className="text-center">
                  <p className="text-sm font-semibold text-neutral-500 dark:text-neutral-400">
                    Start Date
                  </p>
                  <p className="text-base md:text-lg font-medium text-neutral-800 dark:text-neutral-200">
                    {new Date(2000, card.start_month - 1, 1).toLocaleString('default', { month: 'long' })}, {card.start_year}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-sm font-semibold text-neutral-500 dark:text-neutral-400">
                    End Date
                  </p>
                  <p className="text-base md:text-lg font-medium text-neutral-800 dark:text-neutral-200">
                    {new Date(2000, card.end_month - 1, 1).toLocaleString('default', { month: 'long' })}, {card.end_year}
                  </p>
                </div>
              </div>

              {/* Assignment Image */}
              {card.assignment_photo && (
                <div className="my-4 md:my-8">
                  <img
                    src={card?.assignment_photo}
                    alt={card?.assignment_name}
                    className="w-full h-48 md:h-64 lg:h-full rounded-lg object-cover shadow-lg"
                  />
                </div>
              )}

              {/* Assignment Description */}
              <div
                className="py-4 md:py-8 prose prose-sm md:prose-base lg:prose-lg prose-neutral dark:prose-invert max-w-none"
                dangerouslySetInnerHTML={{
                  __html: card.assignment_description,
                }}
              />
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      <motion.button
        layoutId={layout ? `card-${card.assignment_name}` : null}
        onClick={handleOpen}
        className={cn(
          "rounded-xl group/bento shadow-xl transition duration-200 shadow-input dark:shadow-none bg-white border border-gray-300 flex flex-col h-[18rem] md:w-[14rem] min-w-[16rem] max-w-[16rem] overflow-hidden",
        )}
      >
        {card?.assignment_photo && (
          <img
            src={card.assignment_photo}
            alt={card.assignment_name}
            className="h-[10rem] w-full object-cover"
          />
        )}

        <div className="group-hover/bento:translate-x-2 transition duration-200 p-4 text-start">
          <div className="font-sans font-semibold bg-gradient-to-tr from-base_900 to-base_500 bg-clip-text text-transparent mb-2">
            {card.assignment_name?.length > 35
              ? card.assignment_name.substring(0, 33) + "..."
              : card.assignment_name}
          </div>

          <div
            dangerouslySetInnerHTML={{
              __html:
                card.assignment_description?.length > 70
                  ? card.assignment_description.substring(0, 70) + "..."
                  : card.assignment_with || "No data available.",
            }}
            className="font-sans font-normal text-neutral-900 text-xs dark:text-neutral-300"
          />
        </div>
      </motion.button>
    </>
  );
};

// BlurImage Component
export const BlurImage = ({ src, alt, className, ...rest }) => {
  const [isLoading, setLoading] = useState(true);
  return (
    <img
      className={`transition duration-300 ${isLoading ? "blur-sm" : "blur-0"} ${className || ""}`}
      onLoad={() => setLoading(false)}
      src={src}
      loading="lazy"
      alt={alt ? alt : "Background of a beautiful view"}
      {...rest}
    />
  );
};

// PropTypes Validation

Carousel.propTypes = {
  items: PropTypes.arrayOf(PropTypes.node).isRequired,
  // initialScroll: PropTypes.number, // Removed as it's not being used and initial scroll is calculated
};

Card.propTypes = {
  card: PropTypes.shape({
    assignment_name: PropTypes.string.isRequired,
    assignment_with: PropTypes.string.isRequired,
    // Changed to start_month/year and end_month/year
    start_month: PropTypes.number.isRequired,
    start_year: PropTypes.number.isRequired,
    end_month: PropTypes.number.isRequired,
    end_year: PropTypes.number.isRequired,
    assignment_photo: PropTypes.string,
    assignment_description: PropTypes.string.isRequired,
    company_logo: PropTypes.string,
  }).isRequired,
  index: PropTypes.number.isRequired,
  layout: PropTypes.bool,
};

BlurImage.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string,
  className: PropTypes.string,
};